import { Table } from '@src/components';
import { RenderColumns } from './columns';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import { Button } from 'antd';
import { RectangleStackIcon } from '@heroicons/react/20/solid';
import { SortDirection } from '@src/types/common';
import { CommissionsList } from '../CommissionsList';
import { useLazyGetReferralsQuery } from '@src/services/affiliate/affiliateApiSlice';
import { Referral } from '@src/services/affiliate/affiliateApiSliceTypes';
import { LIST_LIMIT } from '@src/constants/pagination';
import { UserCirlceAdd } from 'iconsax-react';

export function ReferralsList() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortDirection | null>(null);
  const [showCommissions, setShowCommissions] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Referral>();

  const [getReferrals, { data, isLoading, isFetching }] =
    useLazyGetReferralsQuery({});

  const fetchReferrals = useCallback(
    (page: number = 1) => {
      getReferrals({
        page,
        limit: LIST_LIMIT,
        sortBy: sortBy ?? undefined,
        sortOrder: sortOrder ?? undefined,
      });
    },
    [getReferrals, sortBy, sortOrder]
  );

  useEffect(() => {
    fetchReferrals(page);
  }, [page, sortBy, sortOrder, fetchReferrals]);

  const handleDetailsClick = (item: Referral) => {
    setSelectedItem(item);
    setShowCommissions(true);
  };

  const handleSortChange = (columnKey: string, order: SortDirection | null) => {
    if (order === null) {
      setSortBy(null);
      setSortOrder(null);
    } else {
      setSortBy(columnKey);
      setSortOrder(order);
    }
    setPage(1);
  };

  const columns = RenderColumns({
    t,
    sorterState: { sortBy, sortOrder, onSortChange: handleSortChange },
    onDetails: handleDetailsClick,
  });

  const handleChangePagination = (page: number) => {
    setPage(page);
  };

  return (
    <>
      <Table
        title={
          <div className='flex gap-2 items-center'>
            <UserCirlceAdd variant='Bold' />
            <span>
              {`${data?.total ?? 0} ${
                (data?.total ?? 0) === 1
                  ? t('pages.affiliate.referrals.singularTitle')
                  : t('pages.affiliate.referrals.pluralTitle')
              }`}
            </span>
          </div>
        }
        headerClassName='bg-wl-primary-container rounded-2xl p-6'
        scroll={{ x: 1 }}
        loading={isLoading || isFetching}
        columns={columns}
        onPageChange={handleChangePagination}
        showPagination={true}
        paginationProps={{
          total: data?.total ?? 0,
          current: page,
          pageSize: LIST_LIMIT,
        }}
        dataSource={data?.items ?? []}
      />
      {selectedItem && (
        <ResponsiveModal
          isOpen={showCommissions}
          title={selectedItem.email}
          headerIcon={
            <RectangleStackIcon width={24} className='text-wl-on-background' />
          }
          content={<CommissionsList referredUserId={selectedItem?.id} />}
          onClose={() => setShowCommissions(false)}
          className='w-auto sm:!w-fit !max-w-[880px]'
          footer={
            <Button
              onClick={() => setShowCommissions(false)}
              size='large'
              className='btn-secondary w-full text-wl-on-background'
            >
              {t('buttons.close')}
            </Button>
          }
        />
      )}
    </>
  );
}
